import {
    Building2,
    Hash,
    Calendar,
    User,
    DotSquare,
    Grip,
    MessagesSquare,
    MessagesSquareIcon,
    File,
    Star,
    UsersIcon,
    FileText,
    ChartLine
} from "lucide-react";
import { IProject } from "../core/_models";
import PESLayout2Sidebar from "./PESLayout2Sidebar";
import { useCallback, useEffect, useRef, useState } from "react";
import { MetricCard } from "../../apps/components/MetricCard";
import PanopticEvaluatorModal from "../../Dashboard/components/Evaluation/PanopticEvaluatorModal";
import { IProjectEvaluationSummeryData, } from "../../core/_models";
import EvaluatorList from "../../project/PanopticEvaluationSummary/EvaluatorList";
import { useParams } from "react-router-dom";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { getPanopticEvaluationSummaryData } from "../../core/_panoptic_evaluation";
import { emptyDataValidation } from "../../../../utils/config";
import { CustomDateFormate } from "../../../../utils/date";
import { isAbortedError } from "../../../../utils/global";

type Props = {
    project: IProject;
};

const sections = [
    { id: "project-information", label: "Project Information", icon: ChartLine },
    { id: "summary-of-project", label: "Summary of Project", icon: File },
    { id: "panoptic-principle", label: "Panoptic Principle", icon: Grip },
    { id: "additional-notes", label: "Additional Notes", icon: FileText },
    { id: "recommendations", label: "Recommendations", icon: MessagesSquareIcon },
];

export const getColor = (percentage: number) => {
    if (percentage <= 49) {
        return "#fe3434";
    }

    if (percentage <= 79) {
        return "#f2b63f";
    }

    return "#50CD89";
};

const PESLayout2: React.FC<Props> = ({
    project
}) => {
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [active, setActive] = useState("");
    const [loading, setLoading] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { project_id } = useParams();


    const [SummeryData, setSummeryData] = useState<IProjectEvaluationSummeryData | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleGetPanopticEvaluationSummaryData = useCallback(async () => {
        if (emptyDataValidation.includes(project_id)) return;

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        try {
            const response = await getPanopticEvaluationSummaryData(
                Number(project_id),
                controller.signal
            );

            if (controller.signal.aborted) return;

            if (response.status === 'ok') {
                setSummeryData(response.data);
            } else {
                showSnackbar(response.message, "danger");
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error(error);
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [project_id, showSnackbar]);

    const scrollToSection = (id: string) => {
        const offset = 80; // header + spacing
        const el = sectionRefs.current[id];
        if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({
                top,
                behavior: "smooth",
            });
        }
        setActive(id);
    };

    useEffect(() => {
        handleGetPanopticEvaluationSummaryData();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [handleGetPanopticEvaluationSummaryData]);

    return (
        <div className="d-flex">
            {/* Sidebar */}
            <PESLayout2Sidebar
                sections={sections}
                active={active}
                onClick={scrollToSection}
            />

            {/* Content */}
            <div className="flex-grow-1 p-8 card">

                {/* Key Metrics */}
                <div
                    id="project-information"
                    ref={(el) => {
                        sectionRefs.current["project-information"] = el;
                    }}
                    className="mb-10"
                >
                    <div className="gap-0 mb-3 mb-lg-5 row-cols-1 row-cols-sm-2 row-cols-md-4 row-cols-lg-4 row">
                        <MetricCard
                            label="Project Ref.No"
                            value={SummeryData?.project_reference_number || ""}
                            icon={<Hash className="h-5 w-5 text-white" />}
                        />
                        <MetricCard
                            label="Evaluation Date"
                            value={CustomDateFormate(SummeryData?.project_evaluation_completed_date || "")}
                            icon={<Calendar className="h-5 w-5 text-white" />}
                            color="light-blue"
                        />
                        <MetricCard
                            label="Sector"
                            value={SummeryData?.sector || ""}
                            icon={<Building2 className="h-5 w-5 text-white" />}
                            color="success"
                        />
                        <MetricCard
                            label="Project Owner"
                            value={SummeryData?.project_owner || ""}
                            icon={<User className="h-5 w-5 text-white" />}
                            color="info"
                        />
                    </div>
                </div>
                {/* Summary of Project */}
                <div
                    id="summary-of-project"
                    ref={(el) => {
                        sectionRefs.current["summary-of-project"] = el;
                    }}
                    className="mb-10"
                >
                    <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-5 mb-5">
                        <div className="d-flex align-items-center gap-4 mb-5">
                            <div
                                className="p-3 rounded-4 d-flex"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
                                }}
                            >
                                <File style={{ color: "#2563eb", fontSize: "25px" }} />
                            </div>
                            <div>
                                <h3 className="apple-title">Summary of Project</h3>
                            </div>
                        </div>
                        <div className="text-foreground fs-5">
                            <div dangerouslySetInnerHTML={{ __html: SummeryData?.background_justification || "" }} />
                        </div>
                    </div>

                </div>
                <div
                    id="evaluation-panel"
                    ref={(el) => {
                        sectionRefs.current["evaluation-panel"] = el;
                    }}
                    className="mb-10"
                >
                    <div className="row">
                        <div className="col-md-4 d-flex">
                            <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-5 mb-5 w-100">
                                <div className="d-flex align-items-center gap-4 mb-5">
                                    <div
                                        className="p-3 rounded-4"
                                        style={{
                                            backgroundImage:
                                                "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
                                        }}
                                    >
                                        <Star size={25} fill='#2563eb' color='#2563eb' />
                                    </div>
                                    <div>
                                        <h3 className="apple-title">Outcome of Evaluation</h3>
                                    </div>
                                </div>
                                <div className="text-muted">
                                    <div className="position-relative d-flex justify-content-center align-items-center">
                                        <svg width="250" height="250">
                                            {/* Background circle */}
                                            <circle
                                                cx="125"
                                                cy="125"
                                                r="100"
                                                stroke="#E4E6EF"
                                                strokeWidth="15"
                                                fill="none"
                                            />

                                            {/* Progress circle */}
                                            <circle
                                                cx="125"
                                                cy="125"
                                                r="100"
                                                stroke={getColor(Number(SummeryData?.project_evaluation_percentage) || 0)}
                                                strokeWidth="15"
                                                fill="none"
                                                strokeDasharray={628}
                                                strokeDashoffset={628 - (628 * Number(SummeryData?.project_evaluation_percentage || 0)) / 100}
                                                strokeLinecap="round"
                                                transform="rotate(-90 125 125)"  // start from top
                                            />
                                        </svg>

                                        <div className="position-absolute top-50 start-50 translate-middle fw-bold fs-2">
                                            {SummeryData?.project_evaluation_percentage || 0}%
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-8 d-flex">
                            <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-5 mb-5 w-100">
                                <div className="d-flex align-items-center gap-4 mb-5">
                                    <div
                                        className="p-3 rounded-4"
                                        style={{
                                            backgroundImage:
                                                "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
                                        }}
                                    >
                                        <UsersIcon size={25} fill='#2563eb' color='#2563eb' />
                                    </div>
                                    <div>
                                        <h3 className="apple-title">Evaluation Panel</h3>
                                    </div>
                                </div>
                                <div className="text-muted">
                                    <EvaluatorList showProgress={true} scoreData={SummeryData?.evaluation_by_evaluator || []} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>




                {/* Panoptic Principle */}
                <div
                    id="panoptic-principle"
                    ref={(el) => {
                        sectionRefs.current["panoptic-principle"] = el;
                    }}
                    className="mb-10"
                >
                    <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-5 mb-5">
                        <div className="d-flex align-items-center justify-content-between mb-5">
                            <div className="d-flex align-items-center gap-4 ">
                                <div
                                    className="p-3 rounded-4"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
                                    }}
                                >
                                    <Grip style={{ color: "#2563eb", fontSize: "25px" }} />
                                </div>
                                <div>
                                    <h3 className="apple-title">Panoptic Principle</h3>
                                </div>
                            </div>
                        </div>
                        <PanopticEvaluatorModal data={SummeryData?.evaluator_question_weighted_scores || null} chartheight={250} />
                    </div>
                </div>

                {/* Additional Notes */}
                <div
                    id="additional-notes"
                    ref={(el) => {
                        sectionRefs.current["additional-notes"] = el;
                    }}
                    className="mb-10"
                >

                    <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-5 mb-5">
                        <div className="d-flex align-items-center gap-4 mb-5">
                            <div
                                className="p-3 rounded-4"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
                                }}
                            >

                                <FileText style={{ color: "#2563eb", fontSize: "25px" }} />
                            </div>
                            <div>
                                <h3 className="apple-title">Additional Notes</h3>
                            </div>
                        </div>
                        <div className="text-foreground fs-6">
                            <div dangerouslySetInnerHTML={{ __html: SummeryData?.additional_notes || "" }} />
                        </div>
                    </div>
                </div>

                {/* Recommendations */}
                <div
                    id="recommendations"
                    ref={(el) => {
                        sectionRefs.current["recommendations"] = el;
                    }}
                    className="mb-10"
                >
                    <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-5 mb-5">
                        <div className="d-flex align-items-center gap-4 mb-5">
                            <div
                                className="p-3 rounded-4"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
                                }}
                            >
                                <MessagesSquareIcon style={{ color: "#2563eb", fontSize: "25px" }} />
                            </div>
                            <div>
                                <h3 className="apple-title">Recommendations</h3>
                            </div>
                        </div>
                        <div className="text-foreground fs-5">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: SummeryData?.recommendations || `${Number(SummeryData?.project_evaluation_percentage) <= 49 ? 'project needs complete rework.' : Number(SummeryData?.project_evaluation_percentage) <= 79 ? 'Project returns to Step-2 Of the 4 step prioritisation process to refine identified metrics before resubmission.' : 'Project advances to the bankable portfolio and should be packaged into a standard investor-ready summary as the final step.'}`,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PESLayout2;  